// 用户角色
export enum UserRole {
  // 采购方角色
  COMPANY_ADMIN = 'company_admin',      // 企业管理员（企业首个用户）
  BUYER = 'buyer',                      // 采购员
  WAREHOUSE = 'warehouse',              // 库管员
  PROJECT_MANAGER = 'project_manager',  // 项目主管（项目侧审批）
  PURCHASE_MANAGER = 'purchase_manager', // 总部采购主管
  CENTRAL_PURCHASER = 'central_purchaser', // 集中采购员（汇总下级常规订单）
  DEPUTY_GENERAL_MANAGER = 'deputy_general_manager', // 分管副总
  GENERAL_MANAGER = 'general_manager',   // 总经理
  APPROVAL_LEADER = 'approval_leader',   // 审批领导
  FINANCE = 'finance',                  // 财务人员
  
  // 旧角色（兼容）
  APPROVER = 'approver',                // 审批人（旧）
  REGIONAL_MANAGER = 'regional_manager',// 区域经理（旧）
  
  // 系统角色
  ADMIN = 'admin',                      // 系统管理员
  PLATFORM_ADMIN = 'platform_admin',    // 平台管理员
  YANXUAN_ADMIN = 'yanxuan_admin',      // 严选管理员（/yanxuan 运营；系统管理员默认可进）
  SUPPLIER = 'supplier',                // 供应商
}

// 订单状态（兼容后端大写枚举）
export enum OrderStatus {
  DRAFT = 'DRAFT',
  AUTO_APPROVED = 'AUTO_APPROVED',
  PENDING_MANAGER_APPROVAL = 'PENDING_MANAGER_APPROVAL',
  PENDING_REGIONAL_APPROVAL = 'PENDING_REGIONAL_APPROVAL',
  PENDING_HEADQUARTER_APPROVAL = 'PENDING_HEADQUARTER_APPROVAL',
  PENDING_FINANCE_APPROVAL = 'PENDING_FINANCE_APPROVAL',
  PENDING_OFFLINE_APPROVAL = 'PENDING_OFFLINE_APPROVAL',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CONFIRMED = 'CONFIRMED',
  SHIPPED = 'SHIPPED',
  RECEIVED = 'RECEIVED',
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  SETTLED = 'SETTLED',
  CANCELLED = 'CANCELLED',
}

// 采购模式
export enum PurchaseMode {
  REGULAR = 'REGULAR',
  SPECIAL = 'SPECIAL',
  EMERGENCY = 'EMERGENCY',
}

/** 物业采购审批模式（入驻默认线下审批登记） */
export enum ProcurementApprovalMode {
  PLATFORM = 'PLATFORM',
  OFFLINE_REGISTRATION = 'OFFLINE_REGISTRATION',
}

// 审批级别
export enum ApprovalLevel {
  PROJECT = 'project',
  REGION = 'region',
  GROUP = 'group',
}

// 审批动作
export enum ApprovalAction {
  APPROVE = 'approve',
  REJECT = 'reject',
}

// 组织类型
export enum OrganizationType {
  GROUP = 'group',
  REGION = 'region',
  PROJECT = 'project',
}

// 结算状态
export enum SettlementStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  PAID = 'paid',
}

// 用户信息
export interface User {
  id: number;
  username: string;
  real_name: string;
  role: UserRole;
  org_id?: number;  // 租户用户可能从 organization 获取
  email?: string;
  phone?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  /** 所属组织（用于显示公司/部门名称） */
  organization?: { id: number; name: string } | null;
  last_login_at?: string | null;
  /** 平台代录：须先改密后方可使用密码登录 */
  must_change_password?: boolean;
}

// 登录响应
export interface LoginResponse {
  access_token: string;
  user: User;
}

// 组织信息
export interface Organization {
  id: number;
  name: string;
  /** 供应商开票视角下由订单接口补充的采购方企业抬头与开票档案 */
  invoice_title?: string | null;
  tax_number?: string | null;
  bank_name?: string | null;
  bank_account?: string | null;
  address?: string | null;
  contact_phone?: string | null;
  type: OrganizationType;
  parent_id?: number;
  level: number;
  created_at: string;
  updated_at: string;
  children?: Organization[];
}

// 常购商品
export interface FavoriteProduct {
  id: number;
  user_id: number;
  product_id: number;
  product_variant_id?: number | null;
  org_id: number;
  default_quantity: number;
  purchase_frequency_days?: number;
  last_purchase_at?: string;
  purchase_count: number;
  remark?: string;
  created_at: string;
  updated_at: string;
  product?: Product;
  variant?: ProductVariant | null;
}

/** 补货建议（常购周期 + 历史复购） */
export interface RecommendedReorderItem {
  id: string;
  product_id: number;
  product_variant_id?: number | null;
  default_quantity: number;
  purchase_count: number;
  last_purchase_at?: string | null;
  product?: Product;
  variant_label?: string | null;
  source: 'favorite' | 'history';
  reason: string;
  in_favorites: boolean;
}

// 订单模板
export interface OrderTemplate {
  id: number;
  name: string;
  user_id: number;
  org_id: number;
  items: Array<{
    product_id: number;
    quantity: number;
    product_variant_id?: number | null;
  }>;
  is_default: boolean;
  is_shared?: boolean;
  usage_count: number;
  last_used_at?: string;
  remark?: string;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    real_name?: string;
    username?: string;
  };
}

// 订单模板详情（包含商品信息）
export interface OrderTemplateDetail {
  template: OrderTemplate;
  can_edit?: boolean;
  products: Array<{
    product: Product;
    quantity: number;
    product_variant_id?: number | null;
    variant_label?: string | null;
  }>;
}

// 供应商简要信息（采购端筛选；name 为企业名称）
export interface SupplierBrief {
  id: number;
  name: string;
  username: string;
  /** @deprecated 请使用 name（企业名称） */
  real_name?: string;
}

/** 商品多规格 SKU（价格与库存） */
export interface ProductVariant {
  id: number;
  product_id: number;
  sku: string;
  spec_label: string;
  base_price: number | string;
  ex_factory_price?: number | string | null;
  ref_1688_retail_price?: number | string | null;
  ref_1688_wholesale_price?: number | string | null;
  markup_rate?: number | string | null;
  service_fee_rate?: number | string | null;
  /** 供应商填写：该规格最小起订量一整单预估运费（未税）；仅作参考 */
  supplier_estimated_freight?: number | string | null;
  /** 供应商选填：每增加一个最小起订量，运费约增加（未税）；仅作参考 */
  supplier_freight_increment_per_moq?: number | string | null;
  /** 平台审价：该规格估算运费（未税）；分摊计入售价 */
  platform_estimated_freight?: number | string | null;
  stock_quantity: number;
  /** 规格起订量；为空时使用商品级 min_order_quantity */
  min_order_quantity?: number | null;
  /** 规格计量单位；为空时使用商品级 unit */
  unit?: string | null;
  stock_alert_threshold?: number;
  sort_order?: number;
  is_active?: boolean;
  /** 规格主图；采购端选中该规格时展示 */
  image_url?: string | null;
  /** 规格单件运费（不含税）；无 freight_tiers 时使用（旧模型） */
  freight_fee?: number | string | null;
  /** 规格级运费档位 */
  freight_tiers?: Array<{ min_qty?: number | null; max_qty?: number | null; freight_amount: number }> | null;
}

// 产品信息
export interface Product {
  id: number;
  name: string;
  sku: string;
  spu?: string;
  category: string;
  unit: string;
  base_price: number | string;  // decimal 类型在 TypeORM 中返回字符串
  ex_factory_price?: number | string | null;
  ref_1688_retail_price?: number | string | null;
  ref_1688_wholesale_price?: number | string | null;
  markup_rate?: number | string | null;
  /** 平台审核确认的技术服务费费率（与 markup_rate 一致，按商品设定） */
  service_fee_rate?: number | string | null;
  /** 供应商填写：最小起订量一整单预估运费（未税）；仅作参考 */
  supplier_estimated_freight?: number | string | null;
  /** 供应商选填：每增加一个最小起订量，运费约增加（未税）；仅作参考 */
  supplier_freight_increment_per_moq?: number | string | null;
  /** 平台审价填写的估算运费（未税）；÷ 起订量分摊计入售价 */
  platform_estimated_freight?: number | string | null;
  /** 平台审价默认技术服务费费率（上架审核列表参考，非商品字段） */
  platform_default_markup_rate?: number | string | null;
  /** 增值税率百分数（兼容旧数据） */
  tax_rate?: number | string | null;
  /** 专票增值税率百分数 */
  tax_rate_special?: number | string | null;
  /** 普票增值税率百分数 */
  tax_rate_regular?: number | string | null;
  /** 商品级开票：SPECIAL/REGULAR；null=跟随供应商默认 */
  vat_invoice_type?: 'SPECIAL' | 'REGULAR' | string | null;
  /** 供应商租户默认开票（API 附加）；商品 vat_invoice_type 优先 */
  supplier_default_vat_invoice_type?: 'SPECIAL' | 'REGULAR' | string | null;
  /** 单件运费（不含税）；无 freight_tiers 时使用（旧模型） */
  freight_fee?: number | string | null;
  /** 免运费起订量（件）；>= 此数量不加收运费（旧模型） */
  free_shipping_min_qty?: number | null;
  /** 按订购数量区间的运费档位，金额为订单行运费合计（不含税） */
  freight_tiers?: Array<{ min_qty?: number | null; max_qty?: number | null; freight_amount: number }> | null;
  /** 成本参考价（可空），与 floor_price 共同约束最低售价 */
  cost_price?: number | string | null;
  /** 销售底价（可空） */
  floor_price?: number | string | null;
  description?: string;
  supplier_id: number;
  /** 供应商信息（列表/详情接口返回） */
  supplier?: SupplierBrief;
  /** 详情接口：供应商企业名称（非联系人姓名） */
  supplier_name?: string | null;
  stock_quantity: number;
  min_order_quantity: number;
  /** 发货周期（天） */
  delivery_cycle_days?: number | null;
  image_urls?: string[];
  /** 详情页图文中的图片（主图用 image_urls） */
  detail_image_urls?: string[];
  is_active: boolean;
  /** 供应商草稿（未提交平台审查） */
  is_draft?: boolean;
  /** 平台价格审核 */
  price_audit_status?: 'PENDING' | 'APPROVED' | 'REJECTED';
  /** 已上架改价重审触点（变价/新增规格），审价结束后清空 */
  price_audit_touch?: {
    single_spec_price_changed?: boolean;
    changed_variant_ids?: number[];
    new_variant_ids?: number[];
    removed_skus?: string[];
    changes?: Array<{
      field: string;
      label: string;
      kind: 'text' | 'number' | 'price' | 'images' | 'variant';
      before?: string | number | null;
      after?: string | number | null;
      before_urls?: string[];
      after_urls?: string[];
      added_urls?: string[];
      removed_urls?: string[];
      action?: 'changed' | 'added' | 'removed';
      spec_label?: string;
      sku?: string;
      before_price?: number | null;
      after_price?: number | null;
      before_image?: string | null;
      after_image?: string | null;
    }>;
  } | null;
  price_audit_comment?: string | null;
  submitted_for_price_audit_at?: string | null;
  /** 平台品质/信息审核（与价格均通过才可上架商城） */
  quality_audit_status?: 'PENDING' | 'APPROVED' | 'REJECTED';
  quality_audit_comment?: string | null;
  submitted_for_quality_audit_at?: string | null;
  delist_request_status?: 'NONE' | 'PENDING';
  delist_request_reason?: string | null;
  delist_requested_at?: string | null;
  /** 最近一次平台上架时间 */
  listed_at?: string | null;
  /** 最近一次平台下架时间 */
  delisted_at?: string | null;
  /** 平台暂停销售（仍上架，采购不可下单） */
  sales_paused?: boolean;
  sales_pause_reason?: string | null;
  sales_paused_at?: string | null;
  platform_delist_reason?: string | null;
  /** 有多规格时下单须带 product_variant_id */
  has_variants?: boolean;
  /** 详情/供应商列表返回 */
  variants?: ProductVariant[];
  /** 列表接口：多规格最低价 */
  min_variant_price?: number | string;
  /** 采购专业档案（场景、参数、效果、视频/附件、价格说明） */
  professional_profile?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

// 专属价格
export interface PrivatePrice {
  id: number;
  product_id: number;
  org_id: number;
  price: number;
  effective_date: string;
  expiry_date?: string;
  created_at: string;
  updated_at: string;
  product?: Product;
  organization?: Organization;
}

// 订单项（后端返回 snapshot_price/subtotal，前端兼容 unit_price/total_price）
export interface OrderItem {
  id: number;
  order_id: number;
  product_id: number;
  product_name: string;
  product_variant_id?: number | null;
  specifications?: string;
  unit?: string;
  quantity: number;
  /** 明细行供应商用户 ID */
  supplier_id?: number | null;
  /** 明细行供应商企业名称 */
  supplier_name?: string | null;
  /** 累计已发 */
  shipped_qty?: number;
  /** 累计已收 */
  received_qty?: number;
  /** PENDING | PARTIAL | RECEIVED */
  receive_status?: string;
  received_at?: string | null;
  /** 已退数量（行项部分退） */
  refunded_quantity?: number;
  unit_price?: number;
  snapshot_price?: number;
  total_price?: number;
  subtotal?: number;
  /** 行运费合计 */
  freight_amount?: number;
  /** 行适用增值税率（百分数，如 13 表示 13%） */
  tax_rate?: number | string | null;
  /** 行税费 */
  tax_amount?: number;
  /** 采购方选择的开票类型：SPECIAL=专票，REGULAR=普票 */
  vat_invoice_type?: 'SPECIAL' | 'REGULAR' | string | null;
  /** 双票拆分：出厂单价 */
  ex_factory_unit?: number | null;
  /** 双票拆分：技术服务费单价 */
  markup_unit?: number | null;
  product?: Product;
}

/** 订单审批履历（GET /orders/:id 详情内返回，按时间升序） */
export interface OrderApprovalRecord {
  id: number;
  approver_id: number;
  approver_name?: string | null;
  approval_level: string;
  action: string;
  comment?: string | null;
  approved_at: string;
}

export interface OrderAllocationDetail {
  source_order_id: number;
  source_order_no: string;
  source_org_id: number;
  source_org_name: string;
  source_buyer_id: number;
  source_buyer_name: string;
  product_id: number;
  product_name: string;
  quantity: number;
  delivery_address: string;
}

// 订单
export interface Order {
  id: number;
  order_number?: string;
  order_no?: string;
  buyer_org_id: number;
  buyer_id?: number;
  buyer_user_id?: number;
  purchase_mode?: PurchaseMode;
  total_amount: number;
  /** 未税销售合计 */
  freight_amount?: number;
  /** 税费合计 */
  tax_amount?: number;
  /** 总额（未税销售 + 运费 + 税费） */
  final_amount?: number;
  status: OrderStatus;
  /** 款到发货：须财务确认到账后供应商方可发货 */
  payment_before_ship?: boolean;
  finance_payment_task?: {
    id?: number;
    status?: string;
    payment_voucher_url?: string | null;
    paid_at?: string | null;
    /** 供应商确认款到发货凭证已到账 */
    supplier_payment_verified_at?: string | null;
  } | null;
  /** 按供应商汇总的收款账户与应付金额（采购方为销售价口径） */
  supplier_payments?: Array<{
    supplier_id: number;
    supplier_name: string;
    supplier_org_name?: string | null;
    bank_name?: string | null;
    bank_account?: string | null;
    payment_amount: number;
    voucher_url?: string | null;
    voucher_uploaded_at?: string | null;
    /** 本供付款/确认状态（已确认到账的供应从待付款拆行中移除） */
    payment_status?: string | null;
    verified_at?: string | null;
  }>;
  /** 待付款阶段暂存的按供应商凭证 */
  pending_supplier_payment_vouchers?: Array<{
    supplier_id: number;
    voucher_url: string;
    voucher_urls?: string[];
    uploaded_at?: string;
  }> | null;
  supplier_payment_total?: number;
  submit_time?: string;
  approval_time?: string;
  purchase_time?: string;
  receive_time?: string;
  /** 物流单号（格式：物流公司代码:运单号） */
  logistics_no?: string | null;
  /** 供应商发货时录入的实际运费（按运费单据金额）；仅供应商/平台可见 */
  actual_freight_amount?: number | null;
  /** 运费单据 URL 列表；仅供应商/平台可见 */
  actual_freight_voucher_urls?: string[] | null;
  /** 发货批次号 */
  shipment_batch_no?: string | null;
  /** 供应商发货时间 */
  shipped_at?: string | null;
  /** 预计送达日期 */
  expected_delivery_date?: string | null;
  cancel_time?: string;
  remarks?: string;
  purpose?: string;
  /** 收货地址 */
  delivery_address?: string | null;
  contact_person?: string | null;
  contact_phone?: string | null;
  project_name?: string | null;
  /** 退回原因（审批不通过时） */
  reject_reason?: string;
  created_at: string;
  updated_at: string;
  buyer_org?: Organization;
  buyer_organization?: Organization;
  buyer?: User;
  buyer_user?: User;
  items?: OrderItem[];
  allocation_details?: OrderAllocationDetail[] | null;
  invoice_file_url?: string;
  invoice_uploaded_at?: string;
  invoice_received_at?: string | null;
  /** 平台开给供应商的服务费发票 */
  invoice_platform_to_supplier_url?: string | null;
  invoice_platform_uploaded_at?: string | null;
  /** 双票模式：出厂价合计 */
  factory_amount?: number;
  /** 双票模式：技术服务费合计 */
  markup_amount?: number;
  invoice_mode?: string;
  /** 退款工作流（履约后） */
  refund_status?: string;
  refund_amount?: number;
  refund_reason?: string | null;
  refund_requested_at?: string | null;
  refund_approved_at?: string | null;
  refund_platform_remark?: string | null;
  refund_rejected_reason?: string | null;
  refund_completed_at?: string | null;
  refund_supplier_status?: string;
  refund_return_voucher_url?: string | null;
  refund_return_logistics_no?: string | null;
  refund_return_shipped_at?: string | null;
  refund_return_received_at?: string | null;
  /** 供应商线下退款凭证 */
  refund_payment_voucher_url?: string | null;
  refund_payment_voucher_uploaded_at?: string | null;
  refund_payment_confirmed_at?: string | null;
  refund_payment_confirmed_by?: number | null;
  refund_supplier_remark?: string | null;
  refund_supplier_confirm_voucher_url?: string | null;
  refund_supplier_rejected_reason?: string | null;
  refund_items?: Array<{ order_item_id: number; quantity: number; amount: number }>;
  /** 审批记录（详情接口映射后的精简结构） */
  approval_records?: OrderApprovalRecord[];
  /** 线下 OA 审批单号 */
  external_approval_ref?: string | null;
  external_approval_attachments?: string[] | null;
  external_approval_confirmed_at?: string | null;
  /** 主供应商（历史单/单供兼容） */
  supplier_id?: number | null;
  /** 订单×供应商履约单元 */
  supplier_legs?: OrderSupplierLeg[];
  /** 分批发货运单 */
  shipments?: OrderShipment[];
  /** 当前登录供应商视角的本供 Leg（列表/详情） */
  my_supplier_leg?: OrderSupplierLeg | null;
  // 订单多维度状态概览（审批/付款/发货/收货/发票/退款）
  statusOverview?: {
    approvalStatus: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED';
    paymentStatus: 'UNPAID' | 'PENDING' | 'PAID';
    shippingStatus: 'NOT_SHIPPED' | 'SHIPPED' | 'RETURNED';
    receiveStatus: 'NOT_RECEIVED' | 'RECEIVED';
    invoiceStatus: 'NOT_INVOICED' | 'INVOICED';
    refundStatus: 'NONE' | 'REFUNDING' | 'REFUNDED';
  };
}

/** 订单×供应商履约单元 */
export interface OrderSupplierLeg {
  id: number;
  order_id: number;
  supplier_id: number;
  amount: number;
  payment_status: string;
  fulfillment_status: string;
  invoice_status: string;
  voucher_urls?: string[] | null;
  verified_at?: string | null;
  invoice_file_url?: string | null;
  invoice_received_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface OrderShipmentItem {
  id: number;
  shipment_id: number;
  order_item_id: number;
  qty: number;
}

export interface OrderShipment {
  id: number;
  order_id: number;
  supplier_id: number;
  logistics_no?: string | null;
  logistics_company?: string | null;
  shipment_batch_no?: string | null;
  shipped_at?: string | null;
  remark?: string | null;
  /** 快递100 状态码；3=签收 */
  track_state?: string | null;
  /** 物流签收时刻 */
  signed_at?: string | null;
  auto_receive_remind_at?: string | null;
  auto_received_at?: string | null;
  items?: OrderShipmentItem[];
}

// === 库管工作台（P0） ===
export interface WarehouseStock {
  product_id: number;
  product_name: string;
  unit: string;
  quantity: number;
}

export interface CheckRecordItem {
  product_id: number;
  product_name: string;
  unit: string;
  quantity: number;
}

export interface CheckRecord {
  id: number;
  order_id: number;
  order_no: string;
  checked_at: string;
  result: string; // PASSED / FAILED / PARTIAL
  items: CheckRecordItem[];
}

export interface IssueOutResponse {
  success: boolean;
  message: string;
}

// 审批流
export interface ApprovalFlow {
  id: number;
  project_org_id: number;
  level: ApprovalLevel;
  role: UserRole;
  sequence: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// 审批记录
export interface ApprovalRecord {
  id: number;
  order_id: number;
  approver_id: number;
  level: ApprovalLevel;
  action: ApprovalAction;
  comments?: string;
  approval_time: string;
  created_at: string;
  approver?: User;
}

// 结算记录
export interface SettlementRecord {
  id: number;
  settlement_number: string;
  supplier_org_id: number;
  period_start: string;
  period_end: string;
  total_amount: number;
  status: SettlementStatus;
  pdf_url?: string;
  confirmed_at?: string;
  paid_at?: string;
  created_at: string;
  updated_at: string;
  supplier_org?: Organization;
  items?: SettlementItem[];
}

// 结算项
export interface SettlementItem {
  id: number;
  settlement_id: number;
  order_id: number;
  order_number: string;
  buyer_org_name: string;
  total_amount: number;
  order?: Order;
}

// API 响应
export interface ApiResponse<T = any> {
  data?: T;
  message?: string;
  error?: string;
  statusCode?: number;
}

// 分页参数
export interface PaginationParams {
  page?: number;
  limit?: number;
}

// 分页响应
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}
